import { useCallback } from 'react';

import { ApolloQueryResult, useApolloClient } from '@apollo/client';

import {
  ILoyaltyUserOffersQuery,
  ILoyaltyUserRewardsQuery,
  LoyaltyOfferRedemptionType,
  LoyaltyPaymentMethod,
  LoyaltyPromotionType,
  LoyaltyUserOffersDocument,
  LoyaltyUserRewardsDocument,
  ServiceMode,
} from 'generated/graphql-gateway';
import { parseOffer, parseReward } from 'state/global-state/models/loyalty/offers/offers.utils';
import logger from 'utils/logger';

import { LoyaltyAppliedOffer } from '../types';

import {
  IAppliedRewards,
  ICartEntryAdapter,
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
} from './types';
import { createFeedbackMaps } from './utils/helpers';

interface IBaseLoyaltyIncentivesEvaluationParams {
  cartEntries?: ICartEntryAdapter[];
  loyaltyId: string;
  paymentMethod?: LoyaltyPaymentMethod | null;
  serviceMode?: ServiceMode | null;
  storeId?: string | null;
  subtotalAmount?: number;
  options?: {
    incompleteEvaluation?: boolean;
    omitInvalids?: boolean;
    redemptionTypes?: LoyaltyOfferRedemptionType[];
  };
}

interface ILoyaltyOfferEvaluationParams extends IBaseLoyaltyIncentivesEvaluationParams {
  incentiveType: LoyaltyPromotionType.OFFER;
  appliedIncentives: LoyaltyAppliedOffer[];
}

interface ILoyaltyRewardEvaluationParams extends IBaseLoyaltyIncentivesEvaluationParams {
  incentiveType: LoyaltyPromotionType.REWARD;
  appliedIncentives: IAppliedRewards;
}

type EvaluateLoyaltyIncentivesParams =
  | ILoyaltyOfferEvaluationParams
  | ILoyaltyRewardEvaluationParams;

const OFFER_NOT_AVAILABLE_EVALUATION_RESULT: IIncentiveEvaluationResult = {
  code: IncentiveEvaluationErrorCodes.OFFER_NOT_AVAILABLE,
  currentValue: null,
  message: '',
  ruleId: '',
  targetValue: null,
};

const REWARD_NOT_AVAILABLE_EVALUATION_RESULT: IIncentiveEvaluationResult = {
  code: IncentiveEvaluationErrorCodes.REWARD_NOT_AVAILABLE,
  currentValue: null,
  message: '',
  ruleId: '',
  targetValue: null,
};

export const useLoyaltyIncentivesEvaluation = () => {
  const client = useApolloClient();

  const evaluateLoyaltyOffers = useCallback(
    async ({
      appliedIncentives,
      cartEntries,
      loyaltyId,
      paymentMethod,
      serviceMode,
      storeId,
      subtotalAmount,
      options = {},
    }: ILoyaltyOfferEvaluationParams) => {
      const {
        omitInvalids = false,
        incompleteEvaluation = false,
        redemptionTypes = [LoyaltyOfferRedemptionType.STANDARD],
      } = options;
      const parsedAppliedOffers = appliedIncentives.map(offer => parseOffer(offer));
      let evaluationResult;

      const where = {
        appliedIncentives: parsedAppliedOffers,
        cartEntries,
        ids: parsedAppliedOffers.map(({ id }) => id),
        paymentMethod,
        serviceMode,
        storeId,
        subtotalAmount,
        omitInvalids,
        // This boolean forces a full evaluation of all applied offers rules
        isTransactionCreation: !incompleteEvaluation,
        redemptionTypes,
      };

      try {
        const { data }: ApolloQueryResult<ILoyaltyUserOffersQuery> = await client.query({
          query: LoyaltyUserOffersDocument,
          fetchPolicy: 'network-only',
          variables: {
            loyaltyId,
            where,
          },
        });
        const offers = data?.loyaltyUserV2?.offers;
        if (offers) {
          const { incentiveFeedbackMap, incentivesMapById } = createFeedbackMaps(offers);
          // mapping no longer existing offers
          // if the offer didn't come back on the response then it doesn't exist
          parsedAppliedOffers.forEach(({ id }) => {
            if (id && !incentivesMapById[id]) {
              incentiveFeedbackMap[id] = [OFFER_NOT_AVAILABLE_EVALUATION_RESULT];
            }
          });
          evaluationResult = incentiveFeedbackMap;
        }
      } catch (e) {
        logger.error(`Offers evaluation error: ${e}`);

        throw e;
      }

      return evaluationResult;
    },
    [client]
  );

  const evaluateLoyaltyRewards = useCallback(
    async ({
      appliedIncentives,
      cartEntries,
      loyaltyId,
      paymentMethod,
      serviceMode,
      storeId,
      subtotalAmount,
      options = {},
    }: ILoyaltyRewardEvaluationParams) => {
      const { omitInvalids = false, incompleteEvaluation = false } = options;
      const parseAppliedRewards = Object.keys(appliedIncentives).map(cartId =>
        parseReward(appliedIncentives[cartId], cartId)
      );
      let evaluationResult;

      const where = {
        appliedIncentives: parseAppliedRewards,
        cartEntries,
        rewardIds: parseAppliedRewards.map(({ id }) => id),
        paymentMethod,
        serviceMode,
        storeId,
        subtotalAmount,
        omitInvalids,
        // This boolean forces a full evaluation of all applied offers rules
        isTransactionCreation: !incompleteEvaluation,
      };

      try {
        const { data }: ApolloQueryResult<ILoyaltyUserRewardsQuery> = await client.query({
          query: LoyaltyUserRewardsDocument,
          fetchPolicy: 'network-only',
          variables: {
            loyaltyId,
            where,
          },
        });
        const rewards = data?.loyaltyUserV2?.rewards;
        if (rewards) {
          const { incentiveFeedbackMap, incentivesMapById } = createFeedbackMaps(rewards);
          // mapping no longer existing offers
          // if the offer didn't come back on the response then it doesn't exist
          parseAppliedRewards.forEach(({ id }) => {
            if (id && !incentivesMapById[id]) {
              incentiveFeedbackMap[id] = [REWARD_NOT_AVAILABLE_EVALUATION_RESULT];
            }
          });
          evaluationResult = incentiveFeedbackMap;
        }
      } catch (e) {
        logger.error(`Rewards evaluation error: ${e}`);

        throw e;
      }

      return evaluationResult;
    },
    [client]
  );

  const evaluateLoyaltyIncentives = useCallback(
    async (params: EvaluateLoyaltyIncentivesParams) => {
      if (params.incentiveType === LoyaltyPromotionType.OFFER) {
        return await evaluateLoyaltyOffers(params);
      }

      if (params.incentiveType === LoyaltyPromotionType.REWARD) {
        return await evaluateLoyaltyRewards(params);
      }

      return null;
    },
    [evaluateLoyaltyOffers, evaluateLoyaltyRewards]
  );

  return { evaluateLoyaltyIncentives };
};
